<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  steps: readonly string[]
  current: number
  locked: boolean
}>()

defineEmits<{
  select: [step: number]
}>()

const { t } = useI18n({ useScope: 'local' })

const canReturnTo = (target: number): boolean => !props.locked && target < props.current

const stateOf = (target: number): 'current' | 'done' | 'upcoming' => {
  if (target === props.current) {
    return 'current'
  }

  return target < props.current ? 'done' : 'upcoming'
}
</script>

<template>
  <nav class="mb-8 flex flex-wrap items-center gap-2" :aria-label="t('stepNav')">
    <template v-for="(label, index) in steps" :key="label">
      <button
        type="button"
        class="flex items-center gap-2 text-sm font-medium transition-colors"
        :class="[
          stateOf(index + 1) === 'upcoming' ? 'text-ink-3' : 'text-ink',
          canReturnTo(index + 1) ? 'hover:text-action cursor-pointer' : 'cursor-default',
        ]"
        :disabled="!canReturnTo(index + 1)"
        :aria-current="index + 1 === current ? 'step' : undefined"
        @click="$emit('select', index + 1)">
        <span
          class="flex size-7 flex-none items-center justify-center rounded-full text-xs font-bold"
          :class="{
            'bg-action text-white': stateOf(index + 1) === 'current',
            'border-success/20 bg-success-subtle text-success-800 border': stateOf(index + 1) === 'done',
            'bg-surface-3 text-ink-3': stateOf(index + 1) === 'upcoming',
          }">
          <i v-if="stateOf(index + 1) === 'done'" class="pi pi-check text-xs!" aria-hidden="true" />
          <template v-else>{{ index + 1 }}</template>
        </span>
        <span class="hidden sm:inline">{{ label }}</span>
      </button>
      <span v-if="index < steps.length - 1" class="bg-line h-px min-w-3 flex-1" aria-hidden="true" />
    </template>
  </nav>
</template>

<i18n lang="json">
{
  "de": {
    "stepNav": "Schritte"
  },
  "en": {
    "stepNav": "Steps"
  }
}
</i18n>
